import {Seo} from './Seo';
import {
    createArticleSchema,
    createBreadcrumbSchema,
    SITE_URL,
    StructuredData,
} from '@utils/structuredData';

type ArticleSeoConfig = {
    headline: string;
    description: string;
    articleSection: string;
    about: string[];
    pageType?: 'AboutPage';
};

type ArticleSeoProps = {
    slug: string;
    fallbackTitle: string;
    fallbackDescription: string;
    authorName: string;
    datePublished?: string | null;
    image?: string;
};

const ARTICLE_SEO: Record<string, ArticleSeoConfig> = {
    '/article/info': {
        headline:
            'About GoProblems.com — Go Problems, Tsumego and Training Resources',
        description:
            'Learn about GoProblems.com, its interactive Go problem database, community, training resources, and history.',
        articleSection: 'About GoProblems.com',
        about: ['Go problems', 'Tsumego', 'Go training', 'GoProblems.com'],
        pageType: 'AboutPage',
    },
    '/article/problemtypes': {
        headline: 'Types of Go Problems: Tsumego, Tesuji and More',
        description:
            'Learn the main types of Go problems, including life and death, tesuji, joseki, endgame, and whole-board exercises.',
        articleSection: 'Go problem guides',
        about: ['Go problems', 'Tsumego', 'Tesuji', 'Life and death'],
    },
    '/article/solving': {
        headline: 'How to Solve Go Problems and Tsumego',
        description:
            'Learn how to approach Go problems, read variations, choose moves, and use tsumego practice to improve your game.',
        articleSection: 'Go problem guides',
        about: ['How to solve Go problems', 'Tsumego practice', 'Go reading'],
    },
    '/article/problemratings': {
        headline: 'How Go Problem Ratings Work',
        description:
            'Understand how GoProblems.com rates problems and players using solving results and rating data.',
        articleSection: 'Using GoProblems.com',
        about: ['Go problem ratings', 'Elo rating', 'Go skill level'],
    },
    '/article/collections': {
        headline: 'Go Problem Collections: How They Work',
        description:
            'Learn how to find, use, and create Go problem collections for focused tsumego and tactical study.',
        articleSection: 'Using GoProblems.com',
        about: ['Go problem collections', 'Tsumego study', 'Go training'],
    },
    '/article/timetrials': {
        headline: 'Time Trial Mode for Go Problems',
        description:
            'Learn how GoProblems.com Time Trial mode uses timed Go problems to train speed, reading, and decision-making.',
        articleSection: 'Using GoProblems.com',
        about: ['Timed Go problems', 'Tsumego training', 'Go reading practice'],
    },
    '/article/userfeedback': {
        headline: 'Stars, Comments and Flags on Go Problems',
        description:
            'Learn how ratings, comments, and flags help the GoProblems.com community improve Go problems.',
        articleSection: 'Using GoProblems.com',
        about: ['Go problem feedback', 'Go community', 'Problem quality'],
    },
    '/article/training': {
        headline: 'Spaced Repetition Training for Go Problems',
        description:
            'Use spaced repetition to revisit Go problems at the right time and build lasting tsumego recognition skills.',
        articleSection: 'Go training',
        about: ['Spaced repetition', 'Tsumego training', 'Go problems'],
    },
    '/article/creatingproblems': {
        headline: 'How to Create Go Problems',
        description:
            'A guide to creating and contributing useful Go problems for the GoProblems.com community.',
        articleSection: 'Creating Go problems',
        about: ['Create Go problems', 'Tsumego authoring', 'Go problem curation'],
    },
    '/article/problemcreationlifecycle': {
        headline: 'The Go Problem Creation Lifecycle',
        description:
            'Learn how a Go problem moves from draft to review, publication, and community feedback on GoProblems.com.',
        articleSection: 'Creating Go problems',
        about: ['Go problem creation', 'Problem review', 'Go problem curation'],
    },
    '/article/constructionbasics': {
        headline: 'Go Problem Construction Basics',
        description:
            'Learn the fundamentals of constructing clear, useful Go problems and tsumego for players to solve.',
        articleSection: 'Creating Go problems',
        about: ['Go problem construction', 'Tsumego authoring', 'Go teaching'],
    },
    '/article/bestpractices': {
        headline: 'Best Practices for Creating Go Problems',
        description:
            'Guidance for creating accurate, clear, and rewarding Go problems that help players learn.',
        articleSection: 'Creating Go problems',
        about: ['Go problem best practices', 'Tsumego authoring', 'Go teaching'],
    },
    '/article/solutionsignatures': {
        headline: 'Solution Signatures for Go Problems',
        description:
            'Learn how solution signatures define accepted answers and variations in GoProblems.com problems.',
        articleSection: 'Creating Go problems',
        about: ['Go problem solutions', 'Tsumego answers', 'Go problem authoring'],
    },
    '/article/suggestions': {
        headline: 'Go Problem Suggestions and Community Edits',
        description:
            'Learn how to suggest improvements and help maintain accurate, useful Go problems on GoProblems.com.',
        articleSection: 'Creating Go problems',
        about: ['Go problem suggestions', 'Community editing', 'Go problem quality'],
    },
    '/article/local-choice-instructions': {
        headline: 'Local Choice Go Problems: Instructions and Curation',
        description:
            'Learn how local choice Go problems train tactical judgement and how to curate effective positions.',
        articleSection: 'Creating Go problems',
        about: ['Local choice Go problems', 'Go tactics', 'KataGo'],
    },
    '/article/richtext': {
        headline: 'Rich Text Formatting for Go Problems',
        description:
            'Learn how to use rich text formatting to write clear Go problem instructions, hints, and explanations.',
        articleSection: 'Creating Go problems',
        about: ['Rich text', 'Go problem explanations', 'Go problem authoring'],
    },
    '/article/collection-suggestions': {
        headline: 'Collection Suggestions for Go Problems',
        description:
            'Learn how GoProblems.com suggests relevant Go problem collections for focused study and practice.',
        articleSection: 'Using GoProblems.com',
        about: ['Go problem collections', 'Tsumego recommendations', 'Go training'],
    },
    '/article/api': {
        headline: 'GoProblems.com API Access',
        description:
            'Learn how to access GoProblems.com data through the API and build tools for Go problem study.',
        articleSection: 'GoProblems.com API',
        about: ['GoProblems.com API', 'Go problem data', 'Developer tools'],
    },
    '/article/invasions': {
        headline: 'Go Invasion Problems and Scenarios',
        description:
            'Learn about interactive Go invasion problems for studying reductions, invasions, and whole-board judgement.',
        articleSection: 'Go problem guides',
        about: ['Go invasions', 'Go reductions', 'Whole-board judgement'],
    },
    '/article/databreach': {
        headline: 'March 2025 GoProblems.com Data Breach',
        description:
            'Information about the March 2025 GoProblems.com data breach and the actions taken in response.',
        articleSection: 'GoProblems.com announcements',
        about: ['GoProblems.com', 'Data breach', 'Security notice'],
    },
};

export const getArticleSeoConfig = (
    slug: string,
    fallbackTitle: string
): Pick<ArticleSeoConfig, 'headline' | 'description'> => {
    const config = ARTICLE_SEO[`/article/${slug}`];

    return config ?? {headline: fallbackTitle, description: ''};
};

const createCustomArticleSchema = (
    path: string,
    config: ArticleSeoConfig,
    props: ArticleSeoProps
): StructuredData => {
    const url = `${SITE_URL}${path}`;
    const schema = createArticleSchema({
        slug: props.slug,
        headline: config.headline,
        description: config.description,
        authorName: props.authorName,
        datePublished: props.datePublished,
        image: props.image,
        aboutPage: config.pageType === 'AboutPage',
    });

    return {
        ...schema,
        articleSection: config.articleSection,
        about: config.about.map(name => ({'@type': 'Thing', name})),
        isAccessibleForFree: true,
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': url,
            url,
        },
    };
};

export const ArticleSeo = ({
    slug,
    fallbackTitle,
    fallbackDescription,
    authorName,
    datePublished,
    image,
}: ArticleSeoProps) => {
    const path = `/article/${slug}`;
    const config = ARTICLE_SEO[path];
    const headline = config?.headline ?? fallbackTitle;
    const description = config?.description ?? fallbackDescription;
    const articleSchema = config
        ? createCustomArticleSchema(path, config, {
              slug,
              fallbackTitle,
              fallbackDescription,
              authorName,
              datePublished,
              image,
          })
        : createArticleSchema({
              slug,
              headline,
              description,
              authorName,
              datePublished,
              image,
          });
    const structuredData = [
        articleSchema,
        createBreadcrumbSchema([
            {name: 'Home', path: '/'},
            {name: headline, path},
        ]),
    ];

    return (
        <Seo
            title={`${headline} | GoProblems.com`}
            description={description}
            canonicalPath={path}
            type={config?.pageType === 'AboutPage' ? 'website' : 'article'}
            image={image}
            structuredData={structuredData}
            publishedAt={datePublished ?? undefined}
            author={authorName}
        />
    );
};
